import React, { useLayoutEffect, useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ExcelJS from "exceljs";
import StoreGroupApi from "../../api/StoreGroupApi";
import ProductStoreMatrixApi from "../../api/ProductStoreMatrixApi";
import CustomDialog from "../../components/CustomDialog";

const ProductStoreMatrix = ({ products, stores, mappings }) => {
  const [womensProducts, setWomensProducts] = useState([]);
  const [mensProducts, setMensProducts] = useState([]);
  const [matrixState, setMatrixState] = useState({});
  const [storeGroups, setStoreGroups] = useState({});
  const [eCommerceStore, setECommerceStore] = useState(null);
  const [activeTab, setActiveTab] = useState("mens");
  const [columnOffsets, setColumnOffsets] = useState([]);
  const [searchTerm, setSearchTerm] = useState(""); // State for search input
  const [dialogOpen, setDialogOpen] = useState(false); // State for dialog visibility
  const [successDialog, setSuccessDialog] = useState(false); // Success dialog visibility
  const [errorDialog, setErrorDialog] = useState(false); // Error dialog visibility
  const [errorMessage, setErrorMessage] = useState(""); // State for error messages
  const [allocationName, setAllocationName] = useState(""); // State for allocation name
  const columnRefs = useRef([]);

  // true = auto select variant;
  // false = only select that row
  const SELECT_VARIANTS_ON_ALL = false;

  // Sync the initial mappings with matrixState on component mount or mappings update
  useEffect(() => {
    // Separate products into women's and men's categories
    const womens = products.filter((product) =>
      product.DIVISION.toUpperCase().includes("WOMENS")
    );
    const mens = products.filter(
      (product) => !product.DIVISION.toUpperCase().includes("WOMENS")
    );

    setWomensProducts(womens);
    setMensProducts(mens);
    setMatrixState(mappings);

    // Identify the ECOMMERCE store
    const eComStore = stores.find((store) => store.STORE_NAME === "ECOMMERCE");
    setECommerceStore(eComStore);

    // Fetch store group data
    const fetchStoreGroups = async () => {
      const groupData = await StoreGroupApi.fetchStoreGroupings();
      const groupMap = { mens: {}, womens: {} };

      groupData.data.forEach((grouping) => {
        const { store_id, groups } = grouping;

        ["mens", "womens"].forEach((division) => {
          if (groups[division]) {
            const groupKey = groups[division];
            if (!groupMap[division][groupKey]) {
              groupMap[division][groupKey] = [];
            }
            const store = stores.find(
              (store) => parseInt(store.id) === store_id
            );
            if (store) {
              groupMap[division][groupKey].push(store);
            }
          }
        });
      });

      setStoreGroups(groupMap);
    };

    fetchStoreGroups();
  }, [products, mappings, stores]);

  const handleSendToAllocation = async () => {
    // Extract valid style_color values from products
    const validStyleColors = new Set(
      products.map((product) => product.STYLE_COLOR)
    );
    try {
      const payload = {
        title: allocationName,
        products,
        stores,
        selectedMappings: Object.entries(matrixState)
          .filter(([key, isSelected]) => {
            if (!isSelected) return false; // Only include selected mappings
            const [style_color] = key.split("-"); // Extract style_color from key
            return validStyleColors.has(style_color); // Check if style_color exists in products
          })
          .map(([key]) => {
            const [style_color, store_id] = key.split("-");
            return { style_color, store_id: parseInt(store_id) };
          }),
      };

      const response = await ProductStoreMatrixApi.sendToAllocation(payload);

      // Check if success exists in response
      if (response.success) {
        setSuccessDialog(true);
      } else {
        setErrorDialog(true);
      }
    } catch (error) {
      console.error("Error sending to allocation:", error);
      if (error.response?.data?.error) {
        setErrorMessage(error.response.data.error); // Set the error message from the response
      } else {
        setErrorMessage("An unexpected error occurred. Please try again."); // Default error message
      }
      setErrorDialog(true);
    } finally {
      setDialogOpen(false);
    }
  };

  useLayoutEffect(() => {
    const calculateColumnOffsets = () => {
      const offsets = [];
      let cumulativeOffset = 0;

      columnRefs.current.forEach((ref, index) => {
        if (ref) {
          const width = ref.offsetWidth;
          offsets[index] = cumulativeOffset;
          cumulativeOffset += width;
        }
      });

      setColumnOffsets(offsets);
    };

    calculateColumnOffsets();
    window.addEventListener("resize", calculateColumnOffsets);
    return () => window.removeEventListener("resize", calculateColumnOffsets);
  }, [products]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleCellToggle = async (styleColor, storeId) => {
    const toggledProduct = products.find(
      (product) => product.STYLE_COLOR === styleColor
    );

    if (!toggledProduct) {
      console.error("Product not found for UPC:", styleColor);
      return;
    }

    const styleCode = toggledProduct.STYLE_CODE;
    const isChecked = !matrixState[`${styleColor}-${storeId}`];

    // If the checkbox is being unchecked, toggle only the clicked checkbox
    if (!isChecked) {
      setMatrixState((prevState) => ({
        ...prevState,
        [`${styleColor}-${storeId}`]: isChecked,
      }));

      try {
        // Update only the clicked checkbox on the backend
        await ProductStoreMatrixApi.toggle({
          style_color: styleColor,
          store_id: storeId,
          is_selected: isChecked,
        });
      } catch (error) {
        console.error("Error updating mapping for the checkbox:", error);
        alert("Failed to update mapping. Please try again.");

        // Revert UI changes in case of an error
        setMatrixState((prevState) => ({
          ...prevState,
          [`${styleColor}-${storeId}`]: !isChecked,
        }));
      }

      return;
    }

    // If the checkbox is being checked, toggle all variants
    const productVariants = products.filter(
      (product) => product.STYLE_CODE === styleCode
    );

    setMatrixState((prevState) => {
      const newState = { ...prevState };

      productVariants.forEach((variant) => {
        const variantKey = `${variant.STYLE_COLOR}-${storeId}`;
        newState[variantKey] = isChecked;
      });

      return newState;
    });

    try {
      // Update the backend for all product variants
      await Promise.all(
        productVariants.map((variant) =>
          ProductStoreMatrixApi.toggle({
            style_color: variant.STYLE_COLOR,
            store_id: storeId,
            is_selected: isChecked,
          })
        )
      );
    } catch (error) {
      console.error("Error updating mapping for variants:", error);
      alert("Failed to update mapping for all variants. Please try again.");

      // Revert UI changes if API fails
      setMatrixState((prevState) => {
        const newState = { ...prevState };
        productVariants.forEach((variant) => {
          const variantKey = `${variant.STYLE_COLOR}-${storeId}`;
          newState[variantKey] = !isChecked;
        });
        return newState;
      });
    }
  };

  // Filter products based on the active tab and search term
  const filteredProducts =
    activeTab === "womens"
      ? womensProducts.filter((product) =>
          `${product.STYLE_CODE} ${product.STYLE_DESC_ENG} ${product.COLOR_ENG} ${product.DEPARTMENT} ${product.DIVISION} ${product.GENDER} ${product.COLOR_CODE}`
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        )
      : mensProducts.filter((product) =>
          `${product.STYLE_CODE} ${product.STYLE_DESC_ENG} ${product.COLOR_ENG} ${product.DEPARTMENT} ${product.DIVISION} ${product.GENDER} ${product.COLOR_CODE}`
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        );
  useLayoutEffect(() => {
    const calculateColumnOffsets = () => {
      const offsets = [];
      let cumulativeOffset = 0;

      columnRefs.current.forEach((ref, index) => {
        if (ref) {
          const width = ref.offsetWidth;
          offsets[index] = cumulativeOffset;
          cumulativeOffset += width;
        }
      });

      // Only update state if offsets have changed
      setColumnOffsets((prevOffsets) => {
        const isSame =
          prevOffsets.length === offsets.length &&
          prevOffsets.every((value, index) => value === offsets[index]);
        if (!isSame) {
          return offsets;
        }
        return prevOffsets; // No state update if offsets are unchanged
      });
    };

    calculateColumnOffsets();

    // Add event listener to update offsets on window resize
    window.addEventListener("resize", calculateColumnOffsets);

    // Cleanup on component unmount
    return () => window.removeEventListener("resize", calculateColumnOffsets);
  }, [filteredProducts]); // Trigger recalculation on `filteredProducts` change

  // Export table data to Excel
  const handleDownload = async () => {
    const workbook = new ExcelJS.Workbook();
    const groupColors = [
      "F8CBAD", // Group A
      "C6E0B4", // Group B
      "F4B084", // Group C
      "FFC7CE", // Group D
      "E2EFDA", // Group E
      "FFC000", // Group F
    ];
    const ecommerceColor = "FF0000";
    const productHeaderColor = "BDD7EE";

    const addSheet = (sheetName, products, activeTab) => {
      const worksheet = workbook.addWorksheet(sheetName);

      const productHeaders = [
        "Style",
        "Description",
        "CC",
        "Color",
        "Department",
        "Division",
        "MSRP",
        "Floorset Code",
        "ECOMMERCE",
      ];

      const groupHeadersRow = worksheet.getRow(1);
      const productHeadersRow = worksheet.getRow(2);
      const storeColumnMap = {}; // Map for tracking store column indices

      // Add headers for product details
      productHeaders.forEach((header, index) => {
        const cell1 = groupHeadersRow.getCell(index + 1);
        const cell2 = productHeadersRow.getCell(index + 1);
        cell2.value = header;

        [cell1, cell2].forEach((cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: {
              argb:
                header === "ECOMMERCE" ? ecommerceColor : productHeaderColor,
            },
          };
          cell.font = { name: "Arial", bold: true };
          cell.alignment = { horizontal: "center", vertical: "middle" };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });

        // Apply background color for the entire column for product details
        worksheet
          .getColumn(index + 1)
          .eachCell({ includeEmpty: true }, (cell, rowNumber) => {
            if (rowNumber > 2) {
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: productHeaderColor },
              };
              cell.alignment = { horizontal: "left", vertical: "middle" };
            }
          });
      });

      let colIndex = productHeaders.length + 1;
      const activeGroup = storeGroups[activeTab] || {};

      // Sort groups alphabetically but leave stores unsorted
      const sortedGroups = Object.entries(activeGroup).sort(
        ([groupA], [groupB]) => groupA.localeCompare(groupB)
      );

      // Add group headers and store names
      sortedGroups.forEach(([group, stores], groupIndex) => {
        const startCol = colIndex;
        const endCol = colIndex + stores.length - 1;

        // Merge cells for group headers
        worksheet.mergeCells(1, startCol, 1, endCol);
        const cell = groupHeadersRow.getCell(startCol);
        cell.value = `Group ${group}`;
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: groupColors[groupIndex % groupColors.length] },
        };
        cell.font = { name: "Arial", bold: true };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };

        stores.forEach((store, storeIndex) => {
          const storeCol = colIndex + storeIndex;
          const storeCell = productHeadersRow.getCell(storeCol);
          storeCell.value = store.STORE_NAME;
          storeCell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: groupColors[groupIndex % groupColors.length] },
          };
          storeCell.font = { name: "Arial", bold: true };
          storeCell.alignment = { horizontal: "center" };
          storeCell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };

          storeColumnMap[`${store.id}`] = storeCol; // Map store ID to its column index
        });

        colIndex += stores.length;
      });

      // Add the "Total" column header at the end
      const totalHeaderCol = colIndex;
      const totalHeaderCell = productHeadersRow.getCell(totalHeaderCol);
      const aboveTotalHeaderCell = groupHeadersRow.getCell(totalHeaderCol);
      aboveTotalHeaderCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: productHeaderColor },
      };
      totalHeaderCell.value = "Total";
      totalHeaderCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: productHeaderColor },
      };
      totalHeaderCell.font = { name: "Arial", bold: true };
      totalHeaderCell.alignment = { horizontal: "center", vertical: "middle" };
      totalHeaderCell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };

      // Add product rows
      const totals = Array(totalHeaderCol).fill(0); // Initialize totals for each column
      products.forEach((product, rowIndex) => {
        let productTotal = 0;
        const productRow = [
          product.STYLE_CODE,
          product.STYLE_DESC_ENG,
          product.COLOR_CODE,
          product.COLOR_ENG,
          product.DEPARTMENT,
          product.DIVISION,
          product.STYLE_MSRP,
          product.FLOOR_SET_CODE,
          eCommerceStore
            ? mappings[`${product.STYLE_COLOR}-${eCommerceStore.id}`]
              ? "✓"
              : ""
            : "",
        ];

        // Add store data
        sortedGroups.forEach(([_, stores]) => {
          stores.forEach((store) => {
            const key = `${product.STYLE_COLOR}-${store.id}`;
            const value = mappings[key] ? "✓" : "";
            productRow.push(value);

            if (value === "✓") {
              productTotal += 1; // Increment product total
              totals[storeColumnMap[store.id] - 1] += 1; // Increment store total
            }
          });
        });

        // Add eCommerce totals if applicable
        if (eCommerceStore) {
          const eComKey = `${product.STYLE_COLOR}-${eCommerceStore.id}`;
          if (mappings[eComKey]) {
            totals[productHeaders.length - 1] += 1;
            productTotal += 1;
          }
        }

        productRow.push(productTotal); // Add product total as the last column
        const row = worksheet.addRow(productRow);

        // Left-align specific columns, center-align others
        row.eachCell((cell, cellIndex) => {
          if (cellIndex >= 1 && cellIndex <= 6) {
            cell.alignment = { horizontal: "left", vertical: "middle" };
          } else {
            cell.alignment = { horizontal: "center", vertical: "middle" };
          }
        });
      });

      // Add totals row
      const totalsRow = worksheet.addRow(new Array(colIndex).fill(""));

      // Merge the first 6 cells for "Totals"
      worksheet.mergeCells(totalsRow.number, 1, totalsRow.number, 6);
      const mergedCell = totalsRow.getCell(1);
      mergedCell.value = "Totals:";
      mergedCell.font = { name: "Arial", bold: true };
      mergedCell.alignment = { horizontal: "right", vertical: "middle" };

      // Calculate the total sum for the last column
      let totalSum = 0;

      // Set the totals for the remaining cells
      totals.forEach((total, index) => {
        const cell = totalsRow.getCell(index + 1);
        if (index >= 8) {
          const value = total || 0;
          totalSum += value; // Add to the total sum
          cell.value = value; // Default to 0 if total is empty
          cell.font = { name: "Arial" };
          cell.alignment = { horizontal: "center", vertical: "middle" };
        }
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      // Set the total sum in the last cell
      const lastCell = totalsRow.getCell(colIndex);
      lastCell.value = totalSum; // Set the sum of all previous totals
      lastCell.font = { name: "Arial" };
      lastCell.alignment = { horizontal: "center", vertical: "middle" };
      lastCell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };

      // Adjust column widths dynamically
      worksheet.columns.forEach((column) => {
        let maxLength = 0;
        column.eachCell({ includeEmpty: true }, (cell) => {
          const value = cell.value ? cell.value.toString() : "";
          maxLength = Math.max(maxLength, value.length);
        });
        column.width = maxLength + 3; // Add padding
      });

      // Apply colors after all data is set
      worksheet.columns.forEach((column, colIndex) => {
        // Product details columns (Style to Division)
        if (colIndex >= 0 && colIndex < 8) {
          column.eachCell({ includeEmpty: true }, (cell, rowIndex) => {
            if (rowIndex > 2) {
              // Skip headers
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: productHeaderColor },
              };
              cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
              };
            }
          });
        }
      });

      // Apply group colors to columns after all data is written
      let currentGroupColIndex = productHeaders.length + 1; // Start after product headers

      sortedGroups.forEach(([group, stores], groupIndex) => {
        const groupColor = groupColors[groupIndex % groupColors.length]; // Get group color

        // Iterate through the store columns for this group
        stores.forEach(() => {
          worksheet
            .getColumn(currentGroupColIndex)
            .eachCell({ includeEmpty: true }, (cell, rowIndex) => {
              if (rowIndex > 2) {
                // Skip headers
                cell.fill = {
                  type: "pattern",
                  pattern: "solid",
                  fgColor: { argb: groupColor },
                };
                cell.alignment = { horizontal: "center", vertical: "middle" };
                cell.border = {
                  top: { style: "thin" },
                  left: { style: "thin" },
                  bottom: { style: "thin" },
                  right: { style: "thin" },
                };
              }
            });

          currentGroupColIndex++; // Move to the next column
        });
      });

      // Freeze the first six columns
      worksheet.views = [{ state: "frozen", xSplit: 6 }];
    };

    // Add sheets for mens and womens
    if (mensProducts.length > 0) {
      addSheet("Mens Division", mensProducts, "mens");
    }
    if (womensProducts.length > 0) {
      addSheet("Womens Division", womensProducts, "womens");
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "StoreMatrix.xlsx";
    link.click();
  };

  const handleRowToggle = async (product) => {
    const styleColor = product.STYLE_COLOR;
    const styleCode = product.STYLE_CODE;
    const newState = { ...matrixState };

    // Check if the row is fully selected for all stores
    const allChecked = stores.every(
      (store) => newState[`${styleColor}-${store.id}`]
    );

    // Determine the new selection state for the row
    const newSelectionState = !allChecked;

    const updates = [];
    const affectedKeys = [];

    if (SELECT_VARIANTS_ON_ALL) {
      // Toggle all rows for variants with the same style code
      products.forEach((p) => {
        if (p.STYLE_CODE === styleCode) {
          stores.forEach((store) => {
            const key = `${p.STYLE_COLOR}-${store.id}`;
            if (newState[key] !== newSelectionState) {
              // Only include stores that need to be toggled
              newState[key] = newSelectionState;
              updates.push({
                style_color: p.STYLE_COLOR,
                store_id: store.id,
                is_selected: newSelectionState,
              });
              affectedKeys.push(key);
            }
          });
        }
      });
    } else {
      // Toggle only the current row
      stores.forEach((store) => {
        const key = `${styleColor}-${store.id}`;
        if (newState[key] !== newSelectionState) {
          // Only include stores that need to be toggled
          newState[key] = newSelectionState;
          updates.push({
            style_color: styleColor,
            store_id: store.id,
            is_selected: newSelectionState,
          });
          affectedKeys.push(key);
        }
      });
    }

    // Optimistically update the UI
    setMatrixState((prevState) => ({ ...prevState, ...newState }));

    try {
      // Call the backend API to persist the changes
      await ProductStoreMatrixApi.bulkToggle(updates);
    } catch (error) {
      console.error("Error toggling row:", error);
      alert("Failed to update the selection. Please try again.");

      // Revert UI changes in case of an error
      const revertedState = { ...matrixState };
      affectedKeys.forEach((key) => {
        revertedState[key] = !newSelectionState;
      });
      setMatrixState(revertedState);
    }
  };

  const isRowChecked = (product) => {
    const styleColor = product.STYLE_COLOR;
    return stores.every((store) => matrixState[`${styleColor}-${store.id}`]);
  };

  const handleCheckAll = async () => {
    const newState = {};
    const updates = [];

    // Mark all checkboxes as selected in the UI
    products.forEach((product) => {
      stores.forEach((store) => {
        const key = `${product.STYLE_COLOR}-${store.id}`;
        if (!matrixState[key]) {
          // Only include if the store was previously unchecked
          newState[key] = true;
          updates.push({
            style_color: product.STYLE_COLOR,
            store_id: store.id,
            is_selected: true,
          });
        }
      });
    });

    setMatrixState((prevState) => ({ ...prevState, ...newState }));

    try {
      // Perform the API call only for updates
      if (updates.length > 0) {
        await ProductStoreMatrixApi.bulkToggle(updates);
      }
    } catch (error) {
      console.error("Error toggling all:", error);
      alert("Failed to update all mappings.");
    }
  };

  const handleClearAll = async () => {
    const newState = {};
    const updates = [];

    // Mark all checkboxes as deselected in the UI
    products.forEach((product) => {
      stores.forEach((store) => {
        const key = `${product.STYLE_COLOR}-${store.id}`;
        if (matrixState[key]) {
          // Only include if the store was previously checked
          newState[key] = false;
          updates.push({
            style_color: product.STYLE_COLOR,
            store_id: store.id,
            is_selected: false,
          });
        }
      });
    });

    // Optimistically update the state
    setMatrixState((prevState) => ({ ...prevState, ...newState }));

    try {
      // Perform the API call only for updates
      if (updates.length > 0) {
        await ProductStoreMatrixApi.bulkToggle(updates);
      }
    } catch (error) {
      console.error("Error clearing all:", error);
      alert("Failed to clear all mappings.");
    }
  };

  const handleGroupRowToggle = async (product, group, isChecked) => {
    const groupStores = storeGroups[activeTab][group];
    const updates = [];

    // Update the state optimistically
    setMatrixState((prevState) => {
      const newState = { ...prevState };
      groupStores.forEach((store) => {
        const key = `${product.STYLE_COLOR}-${store.id}`;
        if (matrixState[key] !== isChecked) {
          // Only include stores that are toggled
          newState[key] = isChecked;
          updates.push({
            style_color: product.STYLE_COLOR,
            store_id: store.id,
            is_selected: isChecked,
          });
        }
      });
      return newState;
    });

    try {
      // Call the API only for updates
      if (updates.length > 0) {
        await ProductStoreMatrixApi.bulkToggle(updates);
      }
    } catch (error) {
      console.error("Error toggling group row:", error);
      alert("Failed to update group selection. Please try again.");

      // Revert UI changes in case of an error
      setMatrixState((prevState) => {
        const revertedState = { ...prevState };
        groupStores.forEach((store) => {
          const key = `${product.STYLE_COLOR}-${store.id}`;
          revertedState[key] = !isChecked;
        });
        return revertedState;
      });
    }
  };

  // Check if all stores in the group are selected for the product
  const isGroupRowChecked = (product, group) => {
    const groupStores = storeGroups[activeTab][group];
    return groupStores.every(
      (store) => matrixState[`${product.STYLE_COLOR}-${store.id}`] === true
    );
  };

  return (
    <Box>
      {/* Check All and Clear All Buttons */}
      <Box
        sx={{ display: "flex", justifyContent: "flex-start", marginBottom: 2 }}
      >
        <Button variant="contained" color="primary" onClick={handleCheckAll}>
          Check All
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleClearAll}
          sx={{ marginLeft: 2 }}
        >
          Clear All
        </Button>
      </Box>

      {/* Dialog for Allocation Name */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Set Allocation Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Allocation Name"
            type="text"
            fullWidth
            value={allocationName}
            onChange={(e) => setAllocationName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSendToAllocation}
            disabled={!allocationName.trim()} // Disable if name is empty
          >
            Save and Send
          </Button>
        </DialogActions>
      </Dialog>

      <CustomDialog
        open={successDialog}
        onClose={() => setSuccessDialog(false)}
        title="Success"
        message="Matrix successfully sent to allocation!"
        type="success"
      />

      <CustomDialog
        open={errorDialog}
        onClose={() => setErrorDialog(false)}
        title="Error"
        message={errorMessage}
        type="error"
      />

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        sx={{ marginBottom: 2 }}
      >
        <Tab label={`Men's Division (${mensProducts.length})`} value="mens" />
        <Tab
          label={`Women's Division (${womensProducts.length})`}
          value="womens"
        />
      </Tabs>
      {/* Search and Download Buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 2,
        }}
      >
        <Button
          variant="contained"
          color="success"
          onClick={() => setDialogOpen(true)}
        >
          Send to Allocation
        </Button>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Button variant="contained" color="success" onClick={handleDownload}>
            Download
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer
        component={Paper}
        style={{
          overflowX: "auto",
          overflowY: "auto",
          height: "calc(100vh - 300px)", // Dynamically adjust height based on screen size
          maxHeight: "calc(100vh - 375px)", // Max height based on available viewport
        }}
      >
        <Table stickyHeader size="small" sx={{ border: "0.5px solid #ddd" }}>
          <TableHead>
            <TableRow
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 3,
                background: "#e9edf7", // Group header background color
              }}
            >
              <TableCell
                rowSpan={2}
                ref={(el) => (columnRefs.current[0] = el)}
                style={{
                  position: "sticky",
                  top: 0,
                  left: 0,
                  zIndex: 3,
                  fontWeight: "bold",
                  background: "#f6f7f7",
                  whiteSpace: "nowrap",
                  fontSize: "11px",
                  fontFamily: "'Roboto', sans-serif",
                  border: "0.5px solid #ddd",
                }}
              >
                All
              </TableCell>
              {[
                "Style",
                "Description",
                "CC",
                "Color",
                "Department",
                "Gender",
              ].map((header, index) => (
                <TableCell
                  key={header}
                  rowSpan={2}
                  ref={(el) => (columnRefs.current[index + 1] = el)}
                  style={{
                    position: "sticky",
                    top: 0,
                    left: columnOffsets[index + 1] || 0,
                    zIndex: 3,
                    fontWeight: "bold",
                    background: "#dfe6f3",
                    whiteSpace: "nowrap",
                    fontSize: "11px",
                    fontFamily: "'Roboto', sans-serif",
                    border: "0.5px solid #ddd",
                  }}
                >
                  {header}
                </TableCell>
              ))}
              <TableCell
                rowSpan={2}
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  fontWeight: "bold",
                  background: "#dfe6f3",
                  whiteSpace: "nowrap",
                  fontSize: "11px",
                  fontFamily: "'Roboto', sans-serif",
                  border: "0.5px solid #ddd",
                }}
              >
                MSRP
              </TableCell>
              <TableCell
                rowSpan={2}
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  fontWeight: "bold",
                  background: "#dfe6f3",
                  whiteSpace: "nowrap",
                  fontSize: "11px",
                  fontFamily: "'Roboto', sans-serif",
                  border: "0.5px solid #ddd",
                }}
              >
                Floorset Code
              </TableCell>
              {eCommerceStore && (
                <TableCell
                  rowSpan={2}
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                    background: "#f6f7f7",
                    fontSize: "11px",
                    fontFamily: "'Roboto', sans-serif",
                    border: "0.5px solid #ddd",
                  }}
                >
                  {eCommerceStore.STORE_NAME}
                </TableCell>
              )}
              {Object.entries(storeGroups[activeTab] || {})
                .sort(([groupA], [groupB]) => groupA.localeCompare(groupB))
                .map(([group, stores]) => (
                  <TableCell
                    key={group}
                    colSpan={stores.length + 1}
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                      fontWeight: "bold",
                      background: "#e9edf7",
                      fontSize: "11px",
                      fontFamily: "'Roboto', sans-serif",
                      textAlign: "center",
                      whiteSpace: "nowrap",
                      border: "1px solid #ddd",
                    }}
                  >
                    Group {group}
                  </TableCell>
                ))}
            </TableRow>
            <TableRow
              sx={{
                position: "sticky",
                top: 38, // Offset below group headers
                zIndex: 2,
                background: "#f6f7f7",
              }}
            >
              {Object.entries(storeGroups[activeTab] || {})
                .sort(([groupA], [groupB]) => groupA.localeCompare(groupB))
                .map(([group, stores]) => (
                  <>
                    {/* Add a column for group-level toggle */}
                    <TableCell
                      key={`${group}-toggle`}
                      rowSpan={2}
                      style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 2,
                        fontWeight: "bold",
                        whiteSpace: "nowrap",
                        background: "#e9edf7",
                        fontSize: "11px",
                        textAlign: "center",
                        border: "0.5px solid #ddd",
                      }}
                    >
                      All Group {group}
                    </TableCell>
                    {/* Add columns for store-level toggles */}
                    {stores.map((store) => (
                      <TableCell
                        key={store.id}
                        style={{
                          fontWeight: "bold",
                          whiteSpace: "nowrap",
                          background: "#f6f7f7",
                          fontSize: "11px",
                          fontFamily: "'Roboto', sans-serif",
                          border: "0.5px solid #ddd",
                        }}
                      >
                        {store.STORE_NAME}
                      </TableCell>
                    ))}
                  </>
                ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredProducts.map((product, index) => (
              <TableRow
                key={product.STYLE_COLOR}
                hover
                sx={{
                  backgroundColor: index % 2 === 0 ? "#f9f9f8" : "#ffffff",
                }}
              >
                <TableCell
                  style={{
                    position: "sticky",
                    left: columnOffsets[0] || 0,
                    background: "#f7f7f7",
                    fontSize: "11px",
                    fontFamily: "'Roboto', sans-serif",
                    border: "0.5px solid #ddd",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isRowChecked(product)}
                    onChange={(e) => handleRowToggle(product, e.target.checked)}
                  />
                </TableCell>
                {[
                  product.STYLE_CODE,
                  product.STYLE_DESC_ENG,
                  product.COLOR_CODE,
                  product.COLOR_ENG,
                  product.DEPARTMENT,
                  product.DIVISION,
                ].map((content, colIndex) => (
                  <TableCell
                    key={colIndex}
                    style={{
                      position: "sticky",
                      left: columnOffsets[colIndex + 1] || 0,
                      background: "#dfe6f3",
                      whiteSpace: "nowrap",
                      fontSize: "11px",
                      fontFamily: "'Roboto', sans-serif",
                      border: "0.5px solid #ddd",
                    }}
                  >
                    {content}
                  </TableCell>
                ))}
                <TableCell
                  style={{
                    background: "#dfe6f3",
                    fontSize: "11px",
                    border: "0.5px solid #ddd",
                  }}
                >
                  <b>$</b>
                  {product.STYLE_MSRP}
                </TableCell>
                <TableCell
                  style={{
                    background: "#dfe6f3",
                    fontSize: "11px",
                    border: "0.5px solid #ddd",
                  }}
                >
                  {product.FLOOR_SET_CODE}
                </TableCell>
                {eCommerceStore && (
                  <TableCell
                    align="center"
                    style={{
                      whiteSpace: "nowrap",
                      fontSize: "11px",
                      fontFamily: "'Roboto', sans-serif",
                      border: "0.5px solid #ddd",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={
                        matrixState[
                          `${product.STYLE_COLOR}-${eCommerceStore.id}`
                        ] || false
                      }
                      onChange={() =>
                        handleCellToggle(product.STYLE_COLOR, eCommerceStore.id)
                      }
                    />
                  </TableCell>
                )}
                {Object.entries(storeGroups[activeTab] || {})
                  .sort(([groupA], [groupB]) => groupA.localeCompare(groupB))
                  .map(([group, stores]) => (
                    <>
                      {/* Add a group-level toggle checkbox for the current product */}
                      <TableCell
                        key={`${product.STYLE_COLOR}-${group}-toggle`}
                        style={{
                          textAlign: "center",
                          border: "0.5px solid #ddd",
                          background: "#e9edf7",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isGroupRowChecked(product, group)}
                          onChange={(e) =>
                            handleGroupRowToggle(
                              product,
                              group,
                              e.target.checked
                            )
                          }
                        />
                      </TableCell>
                      {/* Add store-level checkboxes */}
                      {stores.map((store) => (
                        <TableCell
                          key={`${product.STYLE_COLOR}-${store.id}`}
                          style={{
                            textAlign: "center",
                            border: "0.5px solid #ddd",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={
                              matrixState[
                                `${product.STYLE_COLOR}-${store.id}`
                              ] || false
                            }
                            onChange={() =>
                              handleCellToggle(product.STYLE_COLOR, store.id)
                            }
                          />
                        </TableCell>
                      ))}
                    </>
                  ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography
        variant="body2"
        align="left"
        sx={{
          marginTop: 2,
          fontWeight: "bold",
          fontSize: "11px",
          fontFamily: "'Roboto', sans-serif",
        }}
      >
        Showing 1 to {filteredProducts.length} of {products.length} entries
      </Typography>
    </Box>
  );
};

export default ProductStoreMatrix;
